import React from 'react';
/**
 * Lightweight Markdown renderer styled with the site's design tokens.
 * Supports: ## / ### headings, paragraphs, unordered lists (- / *),
 * and inline **bold**, *italic*, and [text](url) links.
 * Intentionally dependency-free to match the project's build setup.
 */
let keySeed = 0;
const nextKey = () => `md-${keySeed++}`;
function renderInline(text: string): React.ReactNode[] {
  const nodes: React.ReactNode[] = [];
  // Matches **bold**, *italic*, or [text](url)
  const pattern = /(\*\*([^*]+)\*\*|\*([^*]+)\*|\[([^\]]+)\]\(([^)]+)\))/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = pattern.exec(text)) !== null) {
    if (match.index > lastIndex) {
      nodes.push(text.slice(lastIndex, match.index));
    }
    if (match[2] !== undefined) {
      nodes.push(<strong key={nextKey()} className="font-bold text-nrs-navy" data-id="element-205" data-mp="3rax9fpi47gqa">
          {match[2]}
        </strong>);
    } else if (match[3] !== undefined) {
      nodes.push(<em key={nextKey()} className="italic" data-id="element-206" data-mp="3vdlj1zenkrob">
          {match[3]}
        </em>);
    } else if (match[4] !== undefined && match[5] !== undefined) {
      nodes.push(<a key={nextKey()} href={match[5]} className="text-nrs-blue underline hover:text-nrs-orange transition-colors" data-id="element-207" data-mp="2i6dwlptvmfzv">
          {match[4]}
        </a>);
    }
    lastIndex = pattern.lastIndex;
  }
  if (lastIndex < text.length) {
    nodes.push(text.slice(lastIndex));
  }
  return nodes;
}
export function Markdown({
  content
}: {
  content: string;
}) {
  const lines = content.replace(/\r\n/g, '\n').split('\n');
  const blocks: React.ReactNode[] = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    // Blank line
    if (line.trim() === '') {
      i++;
      continue;
    }
    // Headings
    if (line.startsWith('### ')) {
      blocks.push(<h3 key={nextKey()} className="text-xl font-bold text-nrs-navy mt-8 mb-3" data-id="element-208" data-mp="1lfbnswuik2op">
          {renderInline(line.slice(4))}
        </h3>);
      i++;
      continue;
    }
    if (line.startsWith('## ')) {
      blocks.push(<h2 key={nextKey()} className="text-2xl font-bold text-nrs-navy mt-10 mb-4" data-id="element-209" data-mp="16r1vb9tliemk">
          {renderInline(line.slice(3))}
        </h2>);
      i++;
      continue;
    }
    if (line.startsWith('# ')) {
      blocks.push(<h2 key={nextKey()} className="text-3xl font-bold text-nrs-navy mt-10 mb-4" data-id="element-210" data-mp="wv8jjsnpap5u">
          {renderInline(line.slice(2))}
        </h2>);
      i++;
      continue;
    }
    // Unordered list
    if (/^\s*[-*]\s+/.test(line)) {
      const items: React.ReactNode[] = [];
      while (i < lines.length && /^\s*[-*]\s+/.test(lines[i])) {
        const itemText = lines[i].replace(/^\s*[-*]\s+/, '');
        items.push(<li key={nextKey()} className="leading-relaxed" data-id="element-211" data-mp="1fsj9unrcu895">
            {renderInline(itemText)}
          </li>);
        i++;
      }
      blocks.push(<ul key={nextKey()} className="list-disc pl-6 space-y-3 mb-6 text-gray-600 text-lg" data-id="element-212" data-mp="28hqgyb4vk660">
          {items}
        </ul>);
      continue;
    }
    // Paragraph (gather consecutive non-blank, non-special lines)
    const paraLines: string[] = [];
    while (i < lines.length && lines[i].trim() !== '' && !lines[i].startsWith('#') && !/^\s*[-*]\s+/.test(lines[i])) {
      paraLines.push(lines[i]);
      i++;
    }
    blocks.push(<p key={nextKey()} className="text-gray-600 text-lg leading-relaxed mb-6" data-id="element-213" data-mp="akgnjirtxxog">
        {renderInline(paraLines.join(' '))}
      </p>);
  }
  return <>{blocks}</>;
}